import { ComponentType } from 'react';

import { BillToAddressCard, CompanyCard, SoldToAddressCard } from '../../../../../components/cards/company';
import { FinancialAttachmentCard, PayerAddressCard } from '../../../../../components/cards/financial';
import { AdditionalAttachmentCard } from '../../../../../components/cards/financial/AttachmentCard';
import PrivacyPolicyCard from '../../../../../components/cards/financial/PrivacyPolicyCard';
import {
  DeliveryDetailsCard,
  DeliveryInstructionsCard,
  ShipToAddressCard
} from '../../../../../components/cards/shipTo';
import CompanyPage from '../../../../../features/CompanyPage';
import FinancialPage from '../../../../../features/FinancialPage';
import ReviewPage from '../../../../../features/ReviewPage';
import ShipToPage from '../../../../../features/ShipTo';
import { CARD_CATEGORIES } from '../../../../../shared/cardConfig/constants';
import {
  ATTACHMENT_FIELDS,
  BILL_TO_ADDRESS_FIELDS,
  COMPANY_FIELDS,
  DELIVERY_DETAILS_FIELDS,
  DELIVERY_INSTRUCTIONS_FIELDS,
  PAYER_ADDRESS_FIELDS,
  SHIP_TO_ADDRESS_FIELDS,
  SOLD_TO_ADDRESS_FIELDS
} from '../../../../../shared/fieldConfig/constants';
import { PAGE_ROUTES } from '../../../../../utils/constants';
import { getShipToSubtitle } from '../../../../../utils/hooksLib';
import billToPages from './billTo';
import payerPages from './payer';
import shipToPages from './shipTo';
import soldToPages from './soldTo';

// ----------------- Types -----------------
export interface Field {
  key: string;
  required?: boolean;
}

export interface Card {
  key: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: ComponentType<any>;
  fields?: Field[];
  hideSameAsBillTo?: boolean;
  fieldWrapper?: string;
  isCardOptional?: boolean;
}

export interface Accordion {
  title: string;
  cards?: Card[];
  count?: number;
  subtitle?: string;
}

export interface Page {
  route: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: ComponentType<any>;
  cards?: Card[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  accordions?: Array<Accordion | ((args: any) => any)>;
  validateFinancials?: boolean;
}

// ******* PAGE AND CARD CONFIGS **********

const companyPageTemplate = {
  route: PAGE_ROUTES.company,
  Component: CompanyPage,
  cards: [
    {
      key: CARD_CATEGORIES.company,
      Component: CompanyCard,
      fields: [{ key: COMPANY_FIELDS.legalName }, { key: COMPANY_FIELDS.tradeDbaName }]
    },
    {
      key: CARD_CATEGORIES.soldTo,
      Component: SoldToAddressCard,
      fields: [
        { key: SOLD_TO_ADDRESS_FIELDS.fiscalId },
        { key: SOLD_TO_ADDRESS_FIELDS.address },
        { key: SOLD_TO_ADDRESS_FIELDS.taxNumber2 },
        { key: SOLD_TO_ADDRESS_FIELDS.taxNumber3 },
        { key: SOLD_TO_ADDRESS_FIELDS.zonaFranca },
        { key: SOLD_TO_ADDRESS_FIELDS.suframa },
        { key: SOLD_TO_ADDRESS_FIELDS.email },
        { key: SOLD_TO_ADDRESS_FIELDS.icmsTaxPayer },
        { key: SOLD_TO_ADDRESS_FIELDS.telephone1 },
        { key: SOLD_TO_ADDRESS_FIELDS.telephone2 }
      ]
    },
    {
      key: CARD_CATEGORIES.billTo,
      Component: BillToAddressCard,
      isCardOptional: true,
      fields: [
        { key: BILL_TO_ADDRESS_FIELDS.address },
        { key: BILL_TO_ADDRESS_FIELDS.taxNumber2 },
        { key: BILL_TO_ADDRESS_FIELDS.taxNumber3 }
      ]
    }
  ]
};

const shipToPageTemplate = {
  route: PAGE_ROUTES.shipTo,
  Component: ShipToPage,
  cards: [
    {
      key: CARD_CATEGORIES.address,
      Component: ShipToAddressCard,
      hideSameAsBillTo: true,
      fields: [
        { key: SHIP_TO_ADDRESS_FIELDS.address },
        { key: SHIP_TO_ADDRESS_FIELDS.taxNumber2 },
        { key: SHIP_TO_ADDRESS_FIELDS.taxNumber3 }
      ],
      fieldWrapper: CARD_CATEGORIES.shipTo
    },
    {
      key: CARD_CATEGORIES.deliveryDetails,
      Component: DeliveryDetailsCard,
      fields: [
        { key: DELIVERY_DETAILS_FIELDS.appointmentRequired },
        { key: DELIVERY_DETAILS_FIELDS.receivingPlantContactName },
        { key: DELIVERY_DETAILS_FIELDS.receivingPlantPhoneNumber },
        { key: DELIVERY_DETAILS_FIELDS.afterHoursContactName },
        { key: DELIVERY_DETAILS_FIELDS.afterHoursPhoneNumber },
        { key: DELIVERY_DETAILS_FIELDS.warehouseReceivingHours }
      ],
      fieldWrapper: CARD_CATEGORIES.shipTo
    },
    {
      key: CARD_CATEGORIES.deliveryInstructions,
      Component: DeliveryInstructionsCard,
      fields: [
        { key: DELIVERY_INSTRUCTIONS_FIELDS.shippingInstructions },
        { key: DELIVERY_INSTRUCTIONS_FIELDS.shippingMarks },
        { key: DELIVERY_INSTRUCTIONS_FIELDS.customsText },
        { key: DELIVERY_INSTRUCTIONS_FIELDS.loadingInstructions },
        { key: DELIVERY_INSTRUCTIONS_FIELDS.unloadingInstructions },
        { key: DELIVERY_INSTRUCTIONS_FIELDS.packingInstructions },
        { key: DELIVERY_INSTRUCTIONS_FIELDS.billOfLadingText },
        { key: DELIVERY_INSTRUCTIONS_FIELDS.textNotaFiscal }
      ],
      fieldWrapper: CARD_CATEGORIES.shipTo
    }
  ]
};

const financialPageTemplate = {
  route: PAGE_ROUTES.financial,
  Component: FinancialPage,
  validateFinancials: true,
  cards: [
    {
      key: CARD_CATEGORIES.payer,
      Component: PayerAddressCard,
      hideSameAsBillTo: true,
      fields: [
        { key: PAYER_ADDRESS_FIELDS.address },
        { key: PAYER_ADDRESS_FIELDS.taxNumber2 },
        { key: PAYER_ADDRESS_FIELDS.taxNumber3 }
      ]
    },
    {
      key: CARD_CATEGORIES.financialAttachmentCard,
      Component: FinancialAttachmentCard,
      fields: [{ key: ATTACHMENT_FIELDS.financialCardAttachment }]
    },
    {
      key: CARD_CATEGORIES.additionalAttachmentCard,
      Component: AdditionalAttachmentCard,
      fields: [{ key: ATTACHMENT_FIELDS.additionalAttachment }]
    },
    {
      key: CARD_CATEGORIES.privacyPolicy,
      Component: PrivacyPolicyCard
    }
  ]
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getShipToCardsForReviewPage({ store }: { store: any }) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return store.customer.shipTo.map((shipTo: any, i: number) => ({
    title: `tabTitles.ship to`,
    count: i + 1,
    subtitle: getShipToSubtitle({ shipToAddress: shipTo.address, store }),
    cards:
      i === 0
        ? shipToPageTemplate.cards
        : shipToPageTemplate.cards.filter(({ key }) => CARD_CATEGORIES.invoiceDelivery !== key)
  }));
}

const reviewPageTemplate = {
  route: PAGE_ROUTES.review,
  Component: ReviewPage,
  accordions: [
    {
      title: 'tabTitles.company',
      cards: companyPageTemplate.cards
    },
    getShipToCardsForReviewPage,
    {
      title: 'tabTitles.financial',
      cards: financialPageTemplate.cards
    }
  ]
};

// *********** TEMPLATE ************

export const templateMap = {
  customer_set_up: {
    pages: [companyPageTemplate, shipToPageTemplate, financialPageTemplate, reviewPageTemplate]
  },
  bill_to: {
    pages: billToPages
  },
  payer: {
    pages: payerPages
  },
  ship_to: {
    pages: shipToPages
  },
  sold_to: {
    pages: soldToPages
  }
};
